import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Plus, Users, Edit, Trash2, Eye, Filter, DollarSign, LayoutGrid, Table, Maximize, Minimize, Building2 } from 'lucide-react';
import type { AppDispatch, RootState } from '@/store';
import {
  fetchTeams,
  createTeam,
  updateTeam,
  deleteTeam,
  setFilters,
  clearError
} from '@/store/slices/teamsSlice';
import { fetchPrograms } from '@/store/slices/programsSlice';
import { DataTable } from '@/components/ui';
import {
  GlassCard,
  GlassButton,
  GlassBadge,
  GlassSelect,
  GlassPageHeader,
  GlassModal,
  GlassConfirmModal,
  GlassLoadingSpinner,
  GlassAlert,
  SectionReveal,
  MetricCard,
  CollapsibleMetricsSection,
} from '@/design-system';
import TeamForm from '../programs/components/TeamForm';
import TeamDetailModal from './components/TeamDetailModal';
import TeamMembersPopover from './components/TeamMembersPopover';
import TeamRosterModal from './components/TeamRosterModal';
import type { Team, Column, CostProfile, AppUser, RateCard } from '@/types';
import { api, costProfilesApi, rateCardsApi } from '@/utils/api';
import { formatCurrencyCompact } from '@/utils/formatters';

const TeamsPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const {
    teams,
    isLoading,
    isCreating,
    isUpdating,
    isDeleting,
    error,
    filters
  } = useSelector((state: RootState) => state.teams);

  const { programs } = useSelector((state: RootState) => state.programs);
  const [costProfiles, setCostProfiles] = useState<CostProfile[]>([]);
  const [isLoadingCostProfiles, setIsLoadingCostProfiles] = useState(false);
  const [users, setUsers] = useState<AppUser[]>([]);

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [isRosterModalOpen, setIsRosterModalOpen] = useState(false);
  const [rosterTeam, setRosterTeam] = useState<Team | null>(null);
  const [selectedTeam, setSelectedTeam] = useState<Team | null>(null);
  const [viewMode, setViewMode] = useState<'grid' | 'table'>('table');
  const [isMaximized, setIsMaximized] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [rateCards, setRateCards] = useState<RateCard[]>([]);

  useEffect(() => {
    dispatch(fetchTeams(filters));
    dispatch(fetchPrograms());
  }, [dispatch, filters]);

  useEffect(() => {
    const loadCostProfiles = async () => {
      setIsLoadingCostProfiles(true);
      const response = await costProfilesApi.getAll();
      if (response.success && response.data) {
        setCostProfiles((response.data as any).costProfiles || response.data);
      }
      setIsLoadingCostProfiles(false);
    };

    loadCostProfiles();
  }, []);

  useEffect(() => {
    const loadLeads = async () => {
      try {
        const response = await api.get('/user-scopes/suggestions?scope=TEAM_LEAD_SCOPE');
        if (response.data.success) {
          const suggestions = response.data.data.suggestions || [];
          const userSuggestions = suggestions
            .filter((s: any) => s.type === 'USER')
            .map((s: any) => ({
              id: s.id,
              firstName: s.label.split(' ')[0],
              lastName: s.label.split(' ').slice(1).join(' '),
            }));
          setUsers(userSuggestions);
        }
      } catch {
        // Silently fail - filter dropdown just won't have lead options
      }
    };
    loadLeads();
  }, []);

  useEffect(() => {
    const loadRateCards = async () => {
      const response = await rateCardsApi.getByYear(new Date().getFullYear());
      if (response.success && response.data) {
        setRateCards((response.data as any).rateCards || response.data);
      }
    };
    loadRateCards();
  }, []);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        dispatch(clearError());
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error, dispatch]);

  const handleCreateTeam = async (data: Partial<Team>) => {
    const result = await dispatch(createTeam(data));
    if (createTeam.fulfilled.match(result)) {
      setIsCreateModalOpen(false);
    }
  };

  const handleUpdateTeam = async (data: Partial<Team>) => {
    if (!selectedTeam) return;
    const result = await dispatch(updateTeam({ id: selectedTeam.id, data }));
    if (updateTeam.fulfilled.match(result)) {
      setIsEditModalOpen(false);
      setSelectedTeam(null);
    }
  };

  const handleDeleteTeam = async () => {
    if (!selectedTeam) return;
    const result = await dispatch(deleteTeam(selectedTeam.id));
    if (deleteTeam.fulfilled.match(result)) {
      setIsDeleteModalOpen(false);
      setSelectedTeam(null);
    }
  };

  const handleFilterChange = (key: string, value: string) => {
    const newFilters = { ...filters };
    if (value) {
      newFilters[key] = value;
    } else {
      delete newFilters[key];
    }
    dispatch(setFilters(newFilters));
  };

  const standardRates: Record<string, number> = {
    onshore: 150,
    offshore: 27,
    blended: 89,
  };

  const getRateForProfile = (costProfileId?: string, costProfile?: CostProfile | null) => {
    const profile = costProfiles.find(cp => cp.id === costProfileId) || costProfile;
    if (!profile) return 0;
    const nameKey = profile.name.toLowerCase();
    const standard = standardRates[nameKey];
    return standard ?? Number(profile.hourlyRate) ?? 0;
  };

  const calculateTeamCost = (team: Team) => {
    if (team.costAllocations && team.costAllocations.length > 0) {
      const totalCost = team.costAllocations.reduce((sum, alloc) => {
        return sum + (getRateForProfile(alloc.costProfileId, alloc.costProfile || undefined) * alloc.resourceCount * 40);
      }, 0);
      return totalCost;
    }

    const rate = getRateForProfile(team.costProfileId, team.costProfile || undefined);
    return team.memberCount * rate * 40;
  };

  const calculateTeamYearlyCost = (team: Team) => {
    return calculateTeamCost(team) * 52;
  };

  const calculateActualWeeklyCost = (team: Team): number | null => {
    if (!team.activeMembers?.length || !rateCards.length) return null;
    let totalHourly = 0;
    for (const m of team.activeMembers) {
      const rc = rateCards.find(r => r.resourceLevelId === m.resourceLevelId);
      if (rc) totalHourly += Number(rc.hourlyRate);
    }
    return totalHourly * 40;
  };

  const calculateActualYearlyCost = (team: Team): number | null => {
    const weekly = calculateActualWeeklyCost(team);
    return weekly !== null ? weekly * 52 : null;
  };

  const programOptions = [
    { value: '', label: 'All Programs' },
    ...programs.map(program => ({
      value: program.id,
      label: program.name
    }))
  ];

  const statusOptions = [
    { value: '', label: 'All Teams' },
    { value: 'true', label: 'Active Only' },
    { value: 'false', label: 'Inactive Only' }
  ];

  const leadOptions = [
    { value: '', label: 'All Team Leads' },
    ...users.map(user => ({
      value: user.id,
      label: `${user.firstName} ${user.lastName}`
    }))
  ];

  // Calculate metrics
  const activeTeams = teams.filter(t => t.isActive).length;
  const totalMembers = teams.reduce((sum, t) => sum + (t.activeResourceCount ?? t.memberCount), 0);
  const totalPlannedWeeklyCost = teams.reduce((sum, t) => sum + calculateTeamCost(t), 0);
  const totalPlannedYearlyCost = totalPlannedWeeklyCost * 52;
  const totalActualWeeklyCost = teams.reduce((sum, t) => sum + (calculateActualWeeklyCost(t) ?? 0), 0);
  const totalActualYearlyCost = totalActualWeeklyCost * 52;

  // Table columns for DataTable view
  const columns: Column<Team>[] = [
    {
      key: 'name',
      header: 'Team Name',
      sortable: true,
      render: (value) => (
        <div className="font-medium text-[var(--text)] text-[13px]">{value}</div>
      )
    },
    {
      key: 'program',
      header: 'Program',
      render: (value) => (
        <span className="text-[var(--muted)]">{value?.name || 'No Program'}</span>
      )
    },
    {
      key: 'memberCount',
      header: 'Members',
      sortable: true,
      render: (_, row) => (
        <TeamMembersPopover
          team={row}
          onClickManage={() => { setRosterTeam(row); setIsRosterModalOpen(true); }}
        />
      )
    },
    {
      key: 'costProfile',
      header: 'Cost Profile',
      render: (value) => (
        value?.name ? (
          <GlassBadge variant="info" size="sm">{value.name}</GlassBadge>
        ) : (
          <span className="text-[var(--muted)] italic">Not assigned</span>
        )
      )
    },
    {
      key: 'plannedWeekly',
      header: 'Planned Weekly',
      render: (_, row) => (
        <div className="flex items-center gap-1">
          <DollarSign className="w-4 h-4 text-[var(--muted)]" />
          <span className="font-semibold text-status-success">${calculateTeamCost(row).toLocaleString()}</span>
        </div>
      )
    },
    {
      key: 'plannedYearly',
      header: 'Planned Yearly',
      render: (_, row) => (
        <div className="flex items-center gap-1">
          <DollarSign className="w-4 h-4 text-[var(--muted)]" />
          <span className="font-semibold text-accent-blue">${calculateTeamYearlyCost(row).toLocaleString()}</span>
        </div>
      )
    },
    {
      key: 'actualWeekly',
      header: 'Actual Weekly',
      render: (_, row) => {
        const cost = calculateActualWeeklyCost(row);
        return (
          <div className="flex items-center gap-1">
            <DollarSign className="w-4 h-4 text-[var(--muted)]" />
            <span className="font-semibold text-emerald-500">
              {cost !== null ? `$${cost.toLocaleString()}` : '--'}
            </span>
          </div>
        );
      }
    },
    {
      key: 'actualYearly',
      header: 'Actual Yearly',
      render: (_, row) => {
        const cost = calculateActualYearlyCost(row);
        return (
          <div className="flex items-center gap-1">
            <DollarSign className="w-4 h-4 text-[var(--muted)]" />
            <span className="font-semibold text-violet-500">
              {cost !== null ? `$${cost.toLocaleString()}` : '--'}
            </span>
          </div>
        );
      }
    },
    {
      key: 'isActive',
      header: 'Status',
      render: (value) => (
        <GlassBadge variant={value ? 'success' : 'default'}>
          {value ? 'Active' : 'Inactive'}
        </GlassBadge>
      )
    }
  ];

  const tableActions = [
    {
      label: 'View',
      icon: Eye,
      onClick: (team: Team) => {
        setSelectedTeam(team);
        setIsDetailModalOpen(true);
      }
    },
    {
      label: 'Edit',
      icon: Edit,
      onClick: (team: Team) => {
        setSelectedTeam(team);
        setIsEditModalOpen(true);
      }
    },
    {
      label: 'Delete',
      icon: Trash2,
      variant: 'danger' as const,
      onClick: (team: Team) => {
        setSelectedTeam(team);
        setIsDeleteModalOpen(true);
      }
    }
  ];

  if (isLoading) {
    return (
      <div className="min-h-screen glass-page-bg flex items-center justify-center">
        <GlassLoadingSpinner size="lg" variant="gradient" label="Loading teams..." />
      </div>
    );
  }

  return (
    <div className="min-h-screen glass-page-bg p-6 lg:p-8">
      {!isMaximized && (
        <>
          <SectionReveal>
            <GlassPageHeader
              title="Teams"
              subtitle="Manage delivery teams and capacity allocation"
              icon={<Users className="w-6 h-6" />}
              iconGradient="blue-violet"
              className="mb-6"
              actions={
                <div className="flex items-center gap-3">
                  <GlassButton
                    variant="secondary"
                    size="sm"
                    onClick={() => setShowFilters(true)}
                    leftIcon={<Filter className="w-4 h-4" />}
                  >
                    Filters
                  </GlassButton>
                  <GlassButton
                    variant={viewMode === 'grid' ? 'primary' : 'ghost'}
                    size="sm"
                    onClick={() => setViewMode('grid')}
                    leftIcon={<LayoutGrid className="w-4 h-4" />}
                  >
                    Grid
                  </GlassButton>
                  <GlassButton
                    variant={viewMode === 'table' ? 'primary' : 'ghost'}
                    size="sm"
                    onClick={() => setViewMode('table')}
                    leftIcon={<Table className="w-4 h-4" />}
                  >
                    Table
                  </GlassButton>
                  {viewMode === 'table' && (
                    <GlassButton
                      variant="secondary"
                      size="sm"
                      onClick={() => setIsMaximized(true)}
                      leftIcon={<Maximize className="w-4 h-4" />}
                    >
                      Maximize
                    </GlassButton>
                  )}
                </div>
              }
            />
          </SectionReveal>

          {/* Metrics Row */}
          <SectionReveal delay={0.1}>
            <CollapsibleMetricsSection storageKey="teams">
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <MetricCard
                  title="Total Teams"
                  value={teams.length}
                  icon={<Users className="w-5 h-5" />}
                  iconColor="violet"
                />
                <MetricCard
                  title="Active Teams"
                  value={activeTeams}
                  icon={<Users className="w-5 h-5" />}
                  iconColor="emerald"
                />
                <MetricCard
                  title="Total Members"
                  value={totalMembers}
                  icon={<Users className="w-5 h-5" />}
                  iconColor="blue"
                />
                <MetricCard
                  title="Planned Weekly"
                  value={formatCurrencyCompact(totalPlannedWeeklyCost)}
                  icon={<DollarSign className="w-5 h-5" />}
                  iconColor="amber"
                />
              </div>
              <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
                <MetricCard
                  title="Planned Yearly"
                  value={formatCurrencyCompact(totalPlannedYearlyCost)}
                  icon={<DollarSign className="w-5 h-5" />}
                  iconColor="cyan"
                />
                <MetricCard
                  title="Actual Weekly"
                  value={formatCurrencyCompact(totalActualWeeklyCost)}
                  icon={<DollarSign className="w-5 h-5" />}
                  iconColor="emerald"
                />
                <MetricCard
                  title="Actual Yearly"
                  value={formatCurrencyCompact(totalActualYearlyCost)}
                  icon={<DollarSign className="w-5 h-5" />}
                  iconColor="violet"
                />
              </div>
            </CollapsibleMetricsSection>
          </SectionReveal>
        </>
      )}

      {!isMaximized && error && (
        <SectionReveal>
          <GlassAlert
            variant="error"
            message={error}
            onClose={() => dispatch(clearError())}
            className="mb-6"
          />
        </SectionReveal>
      )}

      {/* Filters Drawer */}
      {!isMaximized && (
        <div
          className={`fixed inset-0 z-40 transition ${showFilters ? 'pointer-events-auto' : 'pointer-events-none'}`}
          aria-hidden={!showFilters}
        >
          <div
            className={`absolute inset-0 bg-black/25 backdrop-blur-sm transition-opacity ${showFilters ? 'opacity-100' : 'opacity-0'}`}
            onClick={() => setShowFilters(false)}
          />
          <div
            className={`absolute right-0 top-0 h-full w-80 glass-strong border-l border-white/20 shadow-xl transition-transform duration-300 ${
              showFilters ? 'translate-x-0' : 'translate-x-full'
            }`}
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
              <div>
                <h4 className="text-sm font-semibold text-[var(--text)]">Filters</h4>
                <p className="text-xs text-[var(--muted)]">Refine Teams</p>
              </div>
              <GlassButton variant="ghost" size="sm" onClick={() => setShowFilters(false)}>
                Close
              </GlassButton>
            </div>
            <div className="p-5 space-y-5">
              <div className="space-y-2">
                <span className="text-xs font-medium uppercase tracking-wide text-[var(--muted)]">Program</span>
                <GlassSelect
                  value={filters.programId || ''}
                  onChange={(value) => handleFilterChange('programId', value)}
                  options={programOptions}
                  placeholder="Program"
                  fullWidth
                />
              </div>
              <div className="space-y-2">
                <span className="text-xs font-medium uppercase tracking-wide text-[var(--muted)]">Status</span>
                <GlassSelect
                  value={filters.isActive?.toString() || ''}
                  onChange={(value) => handleFilterChange('isActive', value)}
                  options={statusOptions}
                  placeholder="Status"
                  fullWidth
                />
              </div>
              <div className="space-y-2">
                <span className="text-xs font-medium uppercase tracking-wide text-[var(--muted)]">Team Lead</span>
                <GlassSelect
                  value={filters.leadId || ''}
                  onChange={(value) => handleFilterChange('leadId', value)}
                  options={leadOptions}
                  placeholder="Team Lead"
                  fullWidth
                />
              </div>
              <div className="flex gap-2 pt-2">
                <GlassButton
                  variant="ghost"
                  onClick={() => dispatch(setFilters({}))}
                  leftIcon={<Filter className="w-4 h-4" />}
                  fullWidth
                >
                  Clear Filters
                </GlassButton>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Content */}
      {!isMaximized && viewMode === 'grid' ? (
        <SectionReveal delay={0.2}>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {teams.length === 0 ? (
              <div className="col-span-full text-center py-12">
                <Users className="w-12 h-12 text-[var(--muted)] mx-auto mb-4" />
                <h3 className="text-lg font-medium text-[var(--muted)] mb-2">No Teams</h3>
                <p className="text-[var(--muted)]/70">Create your first team to get started</p>
              </div>
            ) : (
              teams.map((team) => (
                <GlassCard key={team.id} hover className="flex flex-col">
                  <div className="p-5">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-[var(--text)]">{team.name}</h3>
                        <p className="text-sm text-[var(--muted)] mt-1">{team.program?.name || 'No Program'}</p>
                      </div>
                      <GlassBadge variant={team.isActive ? 'success' : 'default'}>
                        {team.isActive ? 'Active' : 'Inactive'}
                      </GlassBadge>
                    </div>

                    <div className="space-y-3 mt-4">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-[var(--muted)]">Members:</span>
                        <TeamMembersPopover
                          team={team}
                          onClickManage={() => { setRosterTeam(team); setIsRosterModalOpen(true); }}
                        />
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-[var(--muted)]">Cost Profile:</span>
                        <span className="text-[var(--text)] font-medium">{team.costProfile?.name || 'Not assigned'}</span>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-[var(--muted)]">Planned Weekly:</span>
                        <span className="text-status-success font-semibold flex items-center gap-1">
                          <DollarSign className="w-4 h-4" />
                          ${calculateTeamCost(team).toLocaleString()}
                        </span>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-[var(--muted)]">Planned Yearly:</span>
                        <span className="text-accent-blue font-semibold flex items-center gap-1">
                          <DollarSign className="w-4 h-4" />
                          ${calculateTeamYearlyCost(team).toLocaleString()}
                        </span>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-[var(--muted)]">Actual Weekly:</span>
                        <span className="text-emerald-500 font-semibold flex items-center gap-1">
                          <DollarSign className="w-4 h-4" />
                          {(() => { const c = calculateActualWeeklyCost(team); return c !== null ? `$${c.toLocaleString()}` : '--'; })()}
                        </span>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-[var(--muted)]">Actual Yearly:</span>
                        <span className="text-violet-500 font-semibold flex items-center gap-1">
                          <DollarSign className="w-4 h-4" />
                          {(() => { const c = calculateActualYearlyCost(team); return c !== null ? `$${c.toLocaleString()}` : '--'; })()}
                        </span>
                      </div>
                    </div>

                    <div className="flex items-center justify-between mt-5 pt-4 border-t border-[var(--border)]">
                      <div className="flex items-center gap-1">
                        <GlassButton
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setSelectedTeam(team);
                            setIsDetailModalOpen(true);
                          }}
                          title="View Details"
                        >
                          <Eye className="w-4 h-4" />
                        </GlassButton>
                        <GlassButton
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setSelectedTeam(team);
                            setIsEditModalOpen(true);
                          }}
                          title="Edit Team"
                        >
                          <Edit className="w-4 h-4" />
                        </GlassButton>
                        <GlassButton
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setSelectedTeam(team);
                            setIsDeleteModalOpen(true);
                          }}
                          title="Delete Team"
                        >
                          <Trash2 className="w-4 h-4" />
                        </GlassButton>
                      </div>
                    </div>
                  </div>
                </GlassCard>
              ))
            )}
          </div>
        </SectionReveal>
      ) : null}

      {/* Maximized View - Outside SectionReveal to avoid stacking context */}
      {isMaximized && (
        <>
          <div
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[55]"
            onClick={() => setIsMaximized(false)}
          />
          <GlassCard
            variant="strong"
            className="fixed inset-4 z-[60] flex flex-col overflow-hidden"
            padding="none"
          >
            <div className="flex items-center justify-between px-6 py-4 border-b border-white/10 flex-shrink-0">
              <div>
                <h3 className="text-lg font-semibold text-[var(--text)]">Teams</h3>
                <p className="text-sm text-[var(--muted)]">{teams.length} teams</p>
              </div>
              <GlassButton
                variant="secondary"
                size="sm"
                onClick={() => setIsMaximized(false)}
                leftIcon={<Minimize className="w-4 h-4" />}
              >
                Minimize
              </GlassButton>
            </div>
            <div className="flex-1 overflow-auto">
              <DataTable
                data={teams}
                columns={columns}
                actions={tableActions}
                searchable
                sortable
                pagination
                pageSize={50}
                emptyMessage="No teams found"
              />
            </div>
          </GlassCard>
        </>
      )}

      {/* Normal Table View - Inside SectionReveal with scroll container */}
      {!isMaximized && viewMode === 'table' && (
        <SectionReveal delay={0.2}>
          {!isLoading && teams.length === 0 ? (
            <GlassCard>
              <div className="text-center py-12">
                <Users className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground text-lg">No teams match your filters.</p>
                <p className="text-muted-foreground/60 mt-2">Try adjusting your filters or search criteria.</p>
              </div>
            </GlassCard>
          ) : (
            <GlassCard variant="default" padding="none">
              <div className="max-h-[calc(100vh-350px)] overflow-auto">
                <DataTable
                  data={teams}
                  columns={columns}
                  actions={tableActions}
                  searchable
                  sortable
                  pagination
                  pageSize={15}
                  emptyMessage="No teams found"
                />
              </div>
            </GlassCard>
          )}
        </SectionReveal>
      )}

      {/* Floating New Team button */}
      {!isMaximized && (
        <button
          className="fixed bottom-6 right-6 h-14 w-14 rounded-full shadow-lg bg-gradient-to-r from-violet-500 to-violet-600 text-white hover:shadow-xl hover:scale-105 transition-all duration-200 flex items-center justify-center"
          onClick={() => setIsCreateModalOpen(true)}
          aria-label="Create Team"
        >
          <Plus className="w-6 h-6" />
        </button>
      )}

      {/* Create Modal */}
      <GlassModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        title="Create Team"
        subtitle="Add a new delivery team"
        icon={<Users className="w-5 h-5" />}
        iconGradient="blue-violet"
        size="lg"
      >
        <TeamForm
          programId={filters.programId || programs[0]?.id || ''}
          programs={programs}
          costProfiles={costProfiles}
          onSubmit={handleCreateTeam}
          onCancel={() => setIsCreateModalOpen(false)}
          isLoading={isCreating}
        />
      </GlassModal>

      {/* Edit Modal */}
      <GlassModal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setSelectedTeam(null);
        }}
        title="Edit Team"
        subtitle={selectedTeam?.name}
        icon={<Edit className="w-5 h-5" />}
        iconGradient="emerald"
        size="lg"
      >
        <TeamForm
          initialData={selectedTeam}
          programId={selectedTeam?.programId || programs[0]?.id || ''}
          programs={programs}
          costProfiles={costProfiles}
          onSubmit={handleUpdateTeam}
          onCancel={() => {
            setIsEditModalOpen(false);
            setSelectedTeam(null);
          }}
          isLoading={isUpdating}
        />
      </GlassModal>

      {/* Delete Confirmation Modal */}
      <GlassConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setSelectedTeam(null);
        }}
        onConfirm={handleDeleteTeam}
        title="Delete Team"
        message={`Are you sure you want to delete "${selectedTeam?.name}"? This will remove all allocations and cannot be undone.`}
        confirmLabel="Delete"
        variant="danger"
        isLoading={isDeleting}
      />

      {/* Team Detail Modal */}
      <TeamDetailModal
        isOpen={isDetailModalOpen}
        onClose={() => {
          setIsDetailModalOpen(false);
          setSelectedTeam(null);
        }}
        team={selectedTeam}
      />

      {/* Team Roster Modal */}
      <TeamRosterModal
        isOpen={isRosterModalOpen}
        onClose={() => {
          setIsRosterModalOpen(false);
          setRosterTeam(null);
        }}
        team={rosterTeam}
      />
    </div>
  );
};

export default TeamsPage;
